import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api, ApiError,
  type BroadcastDetail, type GroupMessageRow, type GroupReach, type MemberGroup,
  type ScheduledMessage,
} from '../lib/api'
import { ClipButton, MAX_PICTURES, PictureRow, usePictures } from './PictureTray'
import './group-messages.css'

/** GROUP MESSAGES (item 25, migration 066): a text and/or an email to whole
 *  groups -- coordinators, an area, "your own groups", or every active
 *  member -- from the Messaging tab.
 *
 *  The customer, 6 Oct 2026: "create a message, choose a group, and this
 *  specific group should receive it. Also ... send out an email to a certain
 *  group." Members only (Q1 A); everyone in the group, on duty or not (Q2 A).
 *
 *  NOTHING GOES WITHOUT THE SENDER SEEING WHO. The reach is asked of the
 *  server whenever the groups or the way change, and Send carries the count
 *  he saw: the server refuses a different one rather than quietly texting a
 *  group that grew. Send then asks once more, in a dialog naming the count.
 *  A key made when the message is composed makes a double press one message.
 *
 *  Every class here carries `gm-`: theme.css owns the short names. */

const SEND_BY: { key: 'sms' | 'email' | 'both'; label: string }[] = [
  { key: 'sms', label: 'Text' },
  { key: 'email', label: 'Email' },
  { key: 'both', label: 'Text + Email' },
]
const KIND_HEAD: Record<MemberGroup['kind'], string> = {
  category: 'Categories',
  area: 'Areas',
  list: 'Your own groups',
}
const WAY: Record<string, string> = { sms: 'Text', email: 'Email', both: 'Text + Email' }
/** The Messages report's own words for what became of one send. */
const OUTCOME: Record<string, { word: string; tone: string }> = {
  accepted: { word: 'Accepted', tone: 'gm-ok' },
  delivered: { word: 'Delivered', tone: 'gm-ok' },
  carrier_error: { word: 'Carrier error', tone: 'gm-bad' },
  bounced: { word: 'Bounced', tone: 'gm-bad' },
  failed: { word: 'Failed', tone: 'gm-bad' },
  queued: { word: 'On its way', tone: '' },
}

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** How many texts each person costs: 160 plain characters in one, 153 a
 *  part when longer; a character outside the plain set (an emoji, Hebrew)
 *  makes it 70 and 67. The signature is counted, because it goes. */
const PLAIN = /^[\n\r -~£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¡ÄÖÑÜ§¿äöñüà€^{}\\[\]~|—]*$/
function segments(text: string): { chars: number; parts: number } {
  const chars = [...text].length
  if (chars === 0) return { chars: 0, parts: 0 }
  const plain = PLAIN.test(text)
  const one = plain ? 160 : 70
  const each = plain ? 153 : 67
  return { chars, parts: chars <= one ? 1 : Math.ceil(chars / each) }
}

function stamp(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  if (d.toDateString() === today.toDateString()) return `Today ${time}`
  const y = new Date(today); y.setDate(today.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return `Yesterday ${time}`
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` ${time}`
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}
function people(n: number): string {
  return n === 1 ? '1 person' : `${n} people`
}

type Me = { display_name: string; organization?: { timezone?: string } }

/** Scheduled times are the ORGANIZATION'S clock (067): what the sender picks
 *  and what the list shows, wherever the browser is. */
function tzOf(me: Me): string {
  return me.organization?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
}
function localParts(iso: string, tz: string): { date: string; time: string } {
  const p: Record<string, string> = {}
  for (const x of new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso))) p[x.type] = x.value
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour === '24' ? '00' : p.hour}:${p.minute}` }
}
function whenLocal(iso: string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz, weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso))
}
/** "Fri 9 Oct, 10:00" for a day and time typed on the organization's clock. */
function whenTyped(date: string, time: string): string {
  if (!date || !time) return ''
  const d = new Date(`${date}T00:00:00`)
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}, ${time}`
}
/** "Eastern Daylight Time" rather than "America/Toronto". */
function zoneName(tz: string): string {
  const part = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: 'long' })
    .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')
  return part?.value ?? tz
}
const SCHED_WORD: Record<ScheduledMessage['status'], string> = {
  scheduled: 'Scheduled', sending: 'Going now', sent: 'Sent', cancelled: 'Cancelled', failed: 'Did not go',
}

export function GroupMessages({ me }: { me: Me }) {
  const tz = tzOf(me)
  const [sent, setSent] = useState<GroupMessageRow[]>([])
  const [scheduled, setScheduled] = useState<ScheduledMessage[]>([])
  /** A scheduled message open in the composer, to change or cancel. */
  const [editing, setEditing] = useState<ScheduledMessage | null>(null)
  /** A NEW MESSAGE STARTS EMPTY: bumped once one is sent or scheduled, so the
   *  composer is drawn afresh -- its pictures now belong to that message and
   *  could not go with another (070). */
  const [fresh, setFresh] = useState(0)
  const [listError, setListError] = useState<string | null>(null)
  /** Null: the composer is open. Otherwise the sent message being read. */
  const [openId, setOpenId] = useState<string | null>(null)
  /** ON A PHONE the list and the work cannot sit side by side: this says
   *  which is showing. On a wide screen both always are. */
  const [showing, setShowing] = useState(false)

  const loadSent = useCallback(async () => {
    try {
      setSent(await api.groupMessages())
      setListError(null)
    } catch (e) {
      setListError(e instanceof ApiError ? e.message : 'Could not load the sent messages.')
    }
  }, [])
  const loadScheduled = useCallback(async () => {
    try {
      setScheduled(await api.scheduledList())
    } catch {
      setScheduled([])
    }
  }, [])
  useEffect(() => {
    loadSent(); loadScheduled()
    // Outcomes arrive after the press (receipts, the carrier sweep), and a
    // scheduled one moves to Sent when its minute comes.
    const t = setInterval(() => { loadSent(); loadScheduled() }, 30_000)
    return () => clearInterval(t)
  }, [loadSent, loadScheduled])

  return (
    <div className={`gm-page ${showing ? 'gm-reading' : ''}`}>
      <div className="gm-col gm-list">
        <div className="gm-head">
          <h2>Group messages</h2>
          <span className="gm-spacer" />
          <button type="button" className="btn gm-go"
                  onClick={() => { setOpenId(null); setEditing(null); setShowing(true) }}>
            + New message
          </button>
        </div>
        <div className="gm-cards">
          {listError && <div className="gm-note gm-warn">{listError}</div>}
          {scheduled.length > 0 && (
            <div className="gm-sect"><span className="gm-pill gm-sched">Scheduled</span> not sent yet</div>
          )}
          {scheduled.map((m) => (
            <button type="button" key={m.id}
                    className={`gm-card gm-schedcard ${editing?.id === m.id && openId === null ? 'gm-on' : ''} ${m.status === 'failed' ? 'gm-hasfail' : ''}`}
                    onClick={() => { setEditing(m); setOpenId(null); setShowing(true) }}>
              <span className="gm-t">
                <b>{m.subject ?? m.body.split('\n')[0]}</b>
                <small>{whenLocal(m.send_at, tz)}</small>
              </span>
              <span className="gm-s">{m.everyone ? 'All active members' : m.group_names.join(', ')} · {WAY[m.send_by]}</span>
              <span className="gm-r">
                <span className={`gm-pill ${m.status === 'failed' ? 'gm-bad' : m.status === 'cancelled' ? '' : 'gm-sched'}`}>
                  {SCHED_WORD[m.status]}
                </span>
              </span>
            </button>
          ))}
          {scheduled.length > 0 && sent.length > 0 && <div className="gm-sect">Sent</div>}
          {!listError && sent.length === 0 && scheduled.length === 0 && (
            <div className="gm-empty">Nothing sent to a group yet.</div>
          )}
          {sent.map((m) => (
            <button type="button" key={m.id}
                    className={`gm-card ${m.id === openId ? 'gm-on' : ''} ${m.failed ? 'gm-hasfail' : ''}`}
                    onClick={() => { setOpenId(m.id); setEditing(null); setShowing(true) }}>
              <span className="gm-t">
                <b>{m.subject ?? m.body.split('\n')[0]}</b>
                <small>{stamp(m.sent_at)}</small>
              </span>
              <span className="gm-s">{m.body.split('\n')[0]}</span>
              <span className="gm-r">
                {m.audience.map((a) => <span key={a} className="gm-pill gm-grp">{a}</span>)}
                <span className="gm-pill">{WAY[m.send_by]}</span>
                {m.queued > 0
                  ? <span className="gm-pill">{m.queued} on the way</span>
                  : <span className="gm-pill gm-ok">{people(m.reached)} reached</span>}
                {m.failed > 0 && <span className="gm-pill gm-bad">{m.failed} failed</span>}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="gm-col gm-main">
        {openId === null
          ? <Composer key={editing?.id ?? `new-${fresh}`} me={me} tz={tz} editing={editing}
                      onBack={() => setShowing(false)}
                      onSent={(id) => { loadSent(); setFresh((n) => n + 1); setOpenId(id) }}
                      onScheduled={() => { loadScheduled(); setEditing(null); setFresh((n) => n + 1) }} />
          : <SentDetail id={openId} onBack={() => setShowing(false)} />}
      </div>
    </div>
  )
}

function Composer({ me, tz, editing, onSent, onBack, onScheduled }: {
  me: Me
  tz: string
  /** A scheduled message being changed (067); null for a new message. */
  editing: ScheduledMessage | null
  onSent: (id: string) => void
  /** A phone only: back to the list. */
  onBack: () => void
  onScheduled: () => void
}) {
  const [groups, setGroups] = useState<MemberGroup[]>([])
  const [everyone, setEveryone] = useState(editing?.everyone ?? false)
  const [picked, setPicked] = useState<string[]>(editing?.group_ids ?? [])
  const [sendBy, setSendBy] = useState<'sms' | 'email' | 'both'>(editing?.send_by ?? 'sms')
  const [subject, setSubject] = useState(editing?.subject ?? '')
  const [body, setBody] = useState(editing?.body ?? '')
  /** SEND NOW OR LATER (067). A scheduled one being changed stays "later". */
  const [when, setWhen] = useState<'now' | 'later'>(editing ? 'later' : 'now')
  const [day, setDay] = useState(() => editing
    ? localParts(editing.send_at, tz).date
    : localParts(new Date(Date.now() + 86_400_000).toISOString(), tz).date)
  const [time, setTime] = useState(() => editing ? localParts(editing.send_at, tz).time : '10:00')
  const [askCancel, setAskCancel] = useState(false)
  /** PICTURES (070): the same paperclip and tray as a conversation's, and a
   *  scheduled message's own pictures already in it when it is changed. */
  const tray = usePictures(editing?.pictures ?? [])
  const later = when === 'later'
  // Whoever saves a change signs it: the words that go are his.
  const signer = me.display_name
  const readOnly = !!editing && editing.status !== 'scheduled'
  const [reach, setReach] = useState<GroupReach | null>(null)
  const [reachNote, setReachNote] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** ONE PRESS, ONE MESSAGE: made when this message is composed, kept
   *  across a retried Send, new only after a message actually went. */
  const key = useRef(newKey())
  const ask = useRef(0)

  useEffect(() => {
    api.memberGroups().then(setGroups).catch(() => setGroups([]))
  }, [])

  // A DIFFERENT MESSAGE IS A DIFFERENT KEY. Kept across a retried Send of
  // the same message -- that is what makes a double press one message -- and
  // new the moment the groups, the way or the words change, so a first
  // press that timed out but went cannot swallow an edited second one.
  const pictureIds = tray.ids.join(',')
  useEffect(() => { key.current = newKey() }, [everyone, picked, sendBy, subject, body, when, day, time, pictureIds])

  // WHO IT WOULD REACH, asked again whenever the answer could change. The old
  // answer is dropped AT ONCE, so Send waits for the new count rather than
  // confirming the old one for a different audience. Only the latest
  // question's answer lands.
  useEffect(() => {
    const mine = ++ask.current
    setReach(null)
    if (!everyone && picked.length === 0) {
      setReachNote(null)
      return
    }
    const t = setTimeout(() => {
      api.groupReach({ group_ids: everyone ? [] : picked, everyone, send_by: sendBy })
        .then((r) => { if (ask.current === mine) { setReach(r); setReachNote(null) } })
        .catch((e) => {
          if (ask.current !== mine) return
          setReach(null)
          setReachNote(e instanceof ApiError ? e.message : 'Could not count who it reaches.')
        })
    }, 150)
    return () => clearTimeout(t)
  }, [everyone, picked, sendBy])

  const signature = `\n— ${signer}`
  const hasPictures = tray.ids.length > 0
  const seg = segments(body.trim() ? body.trim() + signature
    : hasPictures ? `— ${signer}` : '')
  const byEmail = sendBy !== 'sms'
  const byText = sendBy !== 'email'

  const byKind = useMemo(() => {
    const out: Record<MemberGroup['kind'], MemberGroup[]> = { category: [], area: [], list: [] }
    for (const g of groups) out[g.kind]?.push(g)
    return out
  }, [groups])

  function toggle(id: string) {
    setEveryone(false)
    setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id])
  }

  // A scheduled one may be aimed at a group with nobody in it YET: who is
  // in the groups is read at the minute it goes (the customer's Q2).
  const ready = !!reach && (later || reach.people > 0)
    && (body.trim().length > 0 || hasPictures) && !tray.busy
    && (!byEmail || subject.trim().length > 0) && (!later || (!!day && !!time))
  const why = !everyone && picked.length === 0 ? 'Choose who it goes to.'
    : !body.trim() && !hasPictures ? 'Write the message, or attach a picture.'
    : tray.busy ? 'A picture is still being added.'
    : byEmail && !subject.trim() ? 'An email needs a subject.'
    : later && (!day || !time) ? 'Choose the day and the time.'
    : !later && reach && reach.people === 0 ? 'Nobody in these groups can be reached that way.'
    : null

  async function schedule() {
    setSending(true)
    setError(null)
    const payload = {
      group_ids: everyone ? [] : picked, everyone, send_by: sendBy,
      subject: byEmail ? subject.trim() : null, body: body.trim(), send_at: `${day}T${time}`,
      picture_ids: tray.ids,
    }
    try {
      if (editing) await api.scheduledUpdate(editing.id, payload)
      else await api.scheduledCreate({ ...payload, request_key: key.current })
      onScheduled()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not schedule it.')
    } finally {
      setSending(false)
    }
  }
  async function cancelIt() {
    if (!editing) return
    setSending(true)
    setError(null)
    try {
      await api.scheduledCancel(editing.id)
      onScheduled()
    } catch (e) {
      setAskCancel(false)
      setError(e instanceof ApiError ? e.message : 'Could not cancel it.')
    } finally {
      setSending(false)
    }
  }

  if (readOnly && editing) {
    return (
      <div className="gm-compose">
        <div className="gm-head">
          <button type="button" className="btn sm gm-back" onClick={onBack}>← Group messages</button>
          <h2>Scheduled message</h2>
          <span className="gm-spacer" />
          <span className={`gm-pill ${editing.status === 'failed' ? 'gm-bad' : 'gm-sched'}`}>{SCHED_WORD[editing.status]}</span>
        </div>
        <div className="gm-body">
          <div className="gm-r">
            {(editing.everyone ? ['All active members'] : editing.group_names).map((g) => (
              <span key={g} className="gm-pill gm-grp">{g}</span>))}
            <span className="gm-pill">{WAY[editing.send_by]}</span>
          </div>
          <SentPictures pictures={editing.pictures} />
          <div className="gm-bubble">{editing.subject && <b>{editing.subject}{'\n'}</b>}{editing.body}{'\n'}— {editing.signed_by_label}</div>
          <div className={`gm-note ${editing.status === 'failed' ? 'gm-warn' : ''}`}>
            {editing.status === 'failed' && <>It did not go at {whenLocal(editing.send_at, tz)}: {(editing.last_error ?? 'no reason was recorded').replace(/\.\s*$/, '')}. Write it again with + New message.</>}
            {editing.status === 'cancelled' && <>Cancelled by {editing.cancelled_by_label ?? 'someone'}. It was set for {whenLocal(editing.send_at, tz)} and never went. Kept here for the record.</>}
            {editing.status === 'sending' && <>It is going out now. It will appear under Sent in a moment.</>}
            {editing.status === 'sent' && <>Sent at {editing.sent_at ? whenLocal(editing.sent_at, tz) : ''}; it is under Sent.</>}
          </div>
        </div>
      </div>
    )
  }

  async function send() {
    if (!reach) return
    setSending(true)
    setError(null)
    try {
      const out = await api.groupSend({
        group_ids: everyone ? [] : picked, everyone, send_by: sendBy,
        subject: byEmail ? subject.trim() : null, body: body.trim(),
        expected_people: reach.people, request_key: key.current,
        picture_ids: tray.ids,
      })
      key.current = newKey()
      setConfirming(false)
      onSent(out.id)
    } catch (e) {
      // Kept open with the same key: pressing again cannot send it twice.
      setConfirming(false)
      setError(e instanceof ApiError ? e.message : 'Could not send it. Nothing was sent twice; try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="gm-compose">
      <div className="gm-head">
        <button type="button" className="btn sm gm-back" onClick={onBack}>← Group messages</button>
        <h2>{editing ? 'Scheduled message' : 'New message to groups'}</h2>
        {editing && <><span className="gm-spacer" /><span className="gm-hint">by {editing.created_by_label}{editing.updated_by_label ? ` · changed by ${editing.updated_by_label}` : ''}</span></>}
      </div>
      <div className="gm-body">
        <section>
          <div className="gm-lab">1. Send to — tick one or more groups</div>
          <div className="gm-kind">Everyone</div>
          <div className="gm-chips">
            <button type="button" className={`gm-chip ${everyone ? 'gm-on' : ''}`}
                    aria-pressed={everyone}
                    onClick={() => { setEveryone(!everyone); setPicked([]) }}>
              All active members
            </button>
          </div>
          {(['category', 'area', 'list'] as const).map((k) => byKind[k].length > 0 && (
            <div key={k}>
              <div className="gm-kind">
                {k === 'list'
                  ? <span className="gm-tag gm-owntag">Your own groups</span>
                  : <><span className="gm-tag gm-rostertag">From the roster</span> {KIND_HEAD[k]}</>}
              </div>
              <div className="gm-chips">
                {byKind[k].map((g) => (
                  <button type="button" key={g.id} aria-pressed={picked.includes(g.id)}
                          className={`gm-chip ${k === 'list' ? 'gm-own' : ''} ${picked.includes(g.id) ? 'gm-on' : ''}`}
                          onClick={() => toggle(g.id)}>
                    {g.name} <b>{g.members ?? ''}</b>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {byKind.list.length === 0 && (
            <p className="gm-hint">Your own groups — the board, the event volunteers — are
              made under Groups, at the top of Messaging.</p>
          )}
        </section>

        <section>
          <div className="gm-lab">2. Send by</div>
          <div className="gm-seg" role="group" aria-label="Send by">
            {SEND_BY.map((s) => (
              <button type="button" key={s.key} aria-pressed={sendBy === s.key}
                      className={sendBy === s.key ? 'gm-on' : ''}
                      onClick={() => setSendBy(s.key)}>{s.label}</button>
            ))}
          </div>
        </section>

        {byEmail && (
          <section>
            <label className="gm-lab" htmlFor="gm-subject">3. Email subject</label>
            <input id="gm-subject" className="gm-input" value={subject} maxLength={200}
                   onChange={(e) => setSubject(e.target.value)} placeholder="What the email is about" />
          </section>
        )}

        <section>
          <label className="gm-lab" htmlFor="gm-text">{byEmail ? '4' : '3'}. Message</label>
          <div className="gm-textrow">
            <textarea id="gm-text" className="gm-input gm-area" value={body} maxLength={3900}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Write the message. It is signed with your name." />
            <ClipButton onFiles={tray.add} disabled={tray.pictures.length >= MAX_PICTURES} />
          </div>
          <PictureRow pictures={tray.pictures} onRemove={tray.remove} />
          <div className="gm-count">
            {seg.chars} characters with your signature
            {byText && seg.parts > 0 && ` · ${plural(seg.parts, 'text message')} each`}
          </div>
        </section>

        <section>
          <div className="gm-lab">{byEmail ? '5' : '4'}. When</div>
          <div className="gm-seg" role="group" aria-label="When">
            {!editing && (
              <button type="button" aria-pressed={!later} className={!later ? 'gm-on' : ''}
                      onClick={() => setWhen('now')}>Send now</button>
            )}
            <button type="button" aria-pressed={later} className={later ? 'gm-on' : ''}
                    onClick={() => setWhen('later')}>Schedule for later</button>
          </div>
          {later && (
            <div className="gm-when">
              <input type="date" className="gm-input" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Day" />
              <input type="time" className="gm-input" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time" />
              <span className="gm-hint">{zoneName(tz)}</span>
            </div>
          )}
        </section>

        <div className="gm-reach" aria-live="polite">
          {reachNote && <span className="gm-warn">{reachNote}</span>}
          {!reachNote && !reach && <span className="gm-hint">Choose who it goes to, and how many people it reaches shows here.</span>}
          {reach && (
            <>
              {later ? 'Today this reaches' : 'Reaches'} <b>{people(reach.people)}</b> in {reach.audience.join(', ')}.<br />
              {byText && `${reach.by_text} by text`}
              {byText && byEmail && ' · '}
              {byEmail && `${reach.by_email} by email`}
              {hasPictures && ` · with ${plural(tray.ids.length, 'picture')}`}
              {hasPictures && byText && (
                <div className="gm-hint">A text with pictures goes as a picture message
                  (MMS), which costs more per person than a plain text.</div>
              )}
              {reach.unreached.length > 0 && (
                <div className="gm-warn">
                  {plural(reach.unreached.length, 'member')} cannot be reached that way and will not get
                  it: {reach.unreached.slice(0, 8).join(', ')}
                  {reach.unreached.length > 8 && ` and ${reach.unreached.length - 8} more`}.
                </div>
              )}
              {later && (
                <div className="gm-hint">It goes to whoever is in the groups at {whenTyped(day, time) || 'that time'} —
                  someone added before then gets it, someone taken out does not.</div>
              )}
              <div className="gm-hint">Signed “— {signer}”. Replies to the text come back into
                Conversations, each in its own private conversation.</div>
            </>
          )}
        </div>

        {error && <div className="gm-note gm-warn">{error}</div>}

        <div className="gm-actions">
          <span className="gm-hint">{why ?? (later ? 'It can be changed or cancelled until it goes.' : 'Nothing is sent until you confirm.')}</span>
          {editing && !askCancel && (
            <button type="button" className="btn" disabled={sending} onClick={() => setAskCancel(true)}>
              Cancel this message
            </button>
          )}
          {editing && askCancel && (
            <>
              <span className="gm-warn">Cancel it? It will not go.</span>
              <button type="button" className="btn" disabled={sending} onClick={() => setAskCancel(false)}>Keep it</button>
              <button type="button" className="btn gm-danger" disabled={sending} onClick={cancelIt}>Yes, cancel it</button>
            </>
          )}
          {later ? (
            <button type="button" className="btn gm-go gm-big" disabled={!ready || sending} onClick={schedule}>
              {editing ? 'Save changes' : `Schedule for ${whenTyped(day, time)}`}
            </button>
          ) : (
            <button type="button" className="btn gm-go gm-big" disabled={!ready || sending}
                    onClick={() => setConfirming(true)}>
              {reach && reach.people > 0 ? `Send to ${people(reach.people)}…` : 'Send…'}
            </button>
          )}
        </div>
      </div>

      {confirming && reach && (
        <div className="gm-modalbg" role="dialog" aria-modal="true" aria-labelledby="gm-confirm-h">
          <div className="gm-modal">
            <h3 id="gm-confirm-h">Send to {people(reach.people)}?</h3>
            <p>
              {reach.audience.join(', ')} · {WAY[sendBy].toLowerCase()}.<br />
              {byText && plural(reach.texts, 'text')}
              {byText && byEmail && ' and '}
              {byEmail && plural(reach.emails, 'email')}
              {hasPictures && `, with ${plural(tray.ids.length, 'picture')},`} go out now. This cannot be taken back.
            </p>
            <div className="gm-actions">
              <button type="button" className="btn" disabled={sending}
                      onClick={() => setConfirming(false)}>Go back</button>
              <button type="button" className="btn gm-go" disabled={sending} onClick={send}>
                {sending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** A group message's pictures (070), from our own copy behind the session. */
function SentPictures({ pictures }: { pictures?: { id: string }[] | null }) {
  if (!pictures?.length) return null
  return (
    <div className="gm-pics">
      {pictures.map((p) => (
        <a key={p.id} href={`/api/messaging/sent-pictures/${p.id}`} target="_blank" rel="noreferrer">
          <img src={`/api/messaging/sent-pictures/${p.id}`} alt="Picture sent" />
        </a>
      ))}
    </div>
  )
}

function SentDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [d, setD] = useState<BroadcastDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setD(null); setErr(null)
    const load = () => api.groupMessage(id)
      .then((x) => { if (alive) setD(x) })
      .catch((e) => { if (alive) setErr(e instanceof ApiError ? e.message : 'Could not open it.') })
    load()
    const t = setInterval(load, 15_000)
    return () => { alive = false; clearInterval(t) }
  }, [id])

  // One row per member: what became of his text(s) and his email.
  const rows = useMemo(() => {
    type Cell = { word: string; tone: string }
    const by = new Map<string, { key: string; label: string; text: Cell[]; email: Cell | null }>()
    for (const x of d?.deliveries ?? []) {
      const label = x.member_label ?? 'a member'
      // By unit AND name: two members called the same are two rows.
      const key = `${x.callsign ?? ''}|${label}`
      const row = by.get(key) ?? { key, label, text: [], email: null }
      const o = OUTCOME[x.outcome] ?? { word: x.outcome, tone: '' }
      const cell = { word: x.reason ? `${o.word} — ${x.reason}` : o.word, tone: o.tone }
      if (x.channel === 'sms') row.text.push(cell); else row.email = cell
      by.set(key, row)
    }
    return [...by.values()]
  }, [d])

  if (err) return <div className="gm-note gm-warn">{err}</div>
  if (!d) return <div className="gm-empty">Loading…</div>
  return (
    <div className="gm-detail">
      <div className="gm-head">
        <button type="button" className="btn sm gm-back" onClick={onBack}>← Sent messages</button>
        <h2>{d.subject ?? d.nature_label ?? 'Group message'}</h2>
        <span className="gm-spacer" />
        <span className="gm-hint">{stamp(d.at)} · by {d.sent_by_label ?? 'the system'}</span>
      </div>
      <div className="gm-body">
        <div className="gm-r">
          {(d.audience ?? []).map((a) => <span key={a} className="gm-pill gm-grp">{a}</span>)}
          {d.send_by && <span className="gm-pill">{WAY[d.send_by]}</span>}
        </div>
        <SentPictures pictures={d.pictures ?? []} />
        <div className="gm-bubble">{d.body}</div>
        <table className="gm-table">
          <thead><tr><th>Member</th><th>Text</th><th>Email</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td>{r.text.length ? r.text.map((c, i) => <span key={i} className={`gm-pill ${c.tone}`}>{c.word}</span>) : <span className="gm-dim">—</span>}</td>
                <td>{r.email ? <span className={`gm-pill ${r.email.tone}`}>{r.email.word}</span> : <span className="gm-dim">—</span>}</td>
              </tr>
            ))}
            {(d.unreached ?? []).map((u, i) => (
              <tr key={`u-${i}`} className="gm-unreached">
                <td>{u}</td>
                <td colSpan={2}><span className="gm-pill">Not sent — no way to reach them by {WAY[d.send_by ?? 'sms'].toLowerCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="gm-hint">“Accepted” means the text server or the mail server took it; the
          text server never says delivered. A carrier error or a bounce shows here when it comes back.</p>
      </div>
    </div>
  )
}
